import React from 'react';
import { connect } from 'react-redux';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/page/detail/PopoverCalendar/PopoverCalendar.selectors';
import { DatePicker } from '@drone-js/date';
import { QueryString } from 'core/utils/queryString/queryString';
import { SearchQueryKey } from 'core-framework/constant/SearchQueryKey';
import { getDateInYmdFormat, getTodayDate } from 'core-framework/util/datetime/date';
import { replaceState } from 'core-framework/window/history';
import { Box, Divider } from '@drone-js/core';
import { getWindowLocation } from 'core/window/windowService';
import CalendarPriceInfoText from 'component/page/detail/PopoverCalendar/CalendarPriceInfoText';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesPopoverCalendarChangeMonthButtonClickEventActivitiesFieldsV1,
    ActivitiesPopoverCalendarClickEventActivitiesFieldsV1,
    ActivitiesPopoverCalendarSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesPopoverCalendarChangeMonthButtonClickEventActivitiesManifestV1,
    ActivitiesPopoverCalendarClickEventActivitiesManifestV1,
    ActivitiesPopoverCalendarSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { addEventListenerToWindow, killEvent, removeEventListenerFromWindow } from 'core/utils/domUtils';
import { ActivityOfferToken } from 'core/type/Details.types';
import { StringFormatter } from '@agoda/react-handyman';

const PopoverCalendar: React.FC<Props> = (props) => {
    const {
        fetchCalendarMonth,
        fetchedMonths,
        activityToken,
        offerOptionToken,
        availabilitySelectedDate,
        offerLevelSelectedDate,
        priceLabels,
        offerLevelPriceLabels,
        isLoading,
        unavailableDates,
        offerLevelUnavailableDates,
        isAvailabilityLoading,
        onSelectDateCallback,
        behaviors,
        isHidePriceInCalendar,
    } = props;
    const isOfferLevel = props.isOfferLevel || false;
    const selectedDate = isOfferLevel ? offerLevelSelectedDate : availabilitySelectedDate;
    const datePickerRef = React.useRef<HTMLElement>(null);
    const popoverCalendarRef = React.useRef<HTMLDivElement>(null);
    const datePickerOpenText = props.cms[316460];

    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onSelectDateCallback && onSelectDateCallback();
            }
        };
        addEventListenerToWindow('keydown', handleKeyDown);
        return () => {
            removeEventListenerFromWindow('keydown', handleKeyDown);
        };
    }, [onSelectDateCallback]);

    React.useEffect(() => {
        const datePickerElement = datePickerRef?.current;
        const activeDay = datePickerElement?.querySelector<HTMLButtonElement>('[aria-selected="true"] > #active-day');
        activeDay?.focus();

        // TODO: Remove custom aria-label once the DatePicker component supports it
        const titleElement = datePickerElement?.querySelector<HTMLButtonElement>('[aria-live="polite"][role="presentation"]');
        titleElement?.setAttribute('aria-label', datePickerOpenText);
    }, [datePickerOpenText]);

    React.useEffect(() => {
        const shouldFetchCalendar = selectedDate && fetchedMonths && fetchedMonths.length <= 1;
        // this useEffect needs to be called only once, so fetchedMonths should not have more than 1 item
        if (shouldFetchCalendar) {
            // in case the selected date is not in the fetched months, fetch the selected month
            fetchCalendarMonth(selectedDate, activityToken, fetchedMonths);
        }
    }, [activityToken, fetchCalendarMonth, fetchedMonths, selectedDate]);

    React.useEffect(() => {
        trackElementEvent<ActivitiesPopoverCalendarSeenEventActivitiesFieldsV1>(
            ActivitiesPopoverCalendarSeenEventActivitiesManifestV1.ids,
            {},
            {
                travel_date: getDateInYmdFormat(selectedDate),
                offer_id: props.selectedOfferId,
                action_element_value: isOfferLevel ? 'offer_level' : 'activity_level',
                activity_id: props.activityId,
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Track seen event should only run once
    }, []);

    const onDateSelect = (_d: Date | undefined, newSelectedDate: Date, _a: Record<string, boolean>, event: React.MouseEvent | React.KeyboardEvent) => {
        killEvent(event);
        // Must-use search from location API because the 'useLocation' does not provide the latest value after updating the location using 'replaceState' method
        const windowLocation = getWindowLocation();
        const query = new QueryString(windowLocation.search);
        const newTravelDate = getDateInYmdFormat(newSelectedDate);

        trackElementEvent<ActivitiesPopoverCalendarClickEventActivitiesFieldsV1>(
            ActivitiesPopoverCalendarClickEventActivitiesManifestV1.ids,
            {},
            { travel_date: newTravelDate, offer_id: props.selectedOfferId, action_element_value: isOfferLevel ? 'offer_level' : 'activity_level', activity_id: props.activityId },
        );

        if (!isOfferLevel) {
            query.set(SearchQueryKey.TravelDate, newTravelDate);
            replaceState(`?${query.toString()}`);
            props.resetSelectedOfferCard();
        }
        // Save some API calls by not feting availability for same travel date.
        const shouldFetchAvailability = getDateInYmdFormat(selectedDate) !== newTravelDate;
        const offerToken: ActivityOfferToken = offerOptionToken ? { activityToken: offerOptionToken, enableOfferFilter: true } : { activityToken, enableOfferFilter: false };
        props.setSelectedDate(newSelectedDate, offerToken, isOfferLevel, props.selectedOfferId, shouldFetchAvailability);
        onSelectDateCallback && onSelectDateCallback();
    };

    const onMonthChange = (month: Date) => {
        trackElementEvent<ActivitiesPopoverCalendarChangeMonthButtonClickEventActivitiesFieldsV1>(
            ActivitiesPopoverCalendarChangeMonthButtonClickEventActivitiesManifestV1.ids,
            {},
            { travel_date: getDateInYmdFormat(month), offer_id: props.selectedOfferId, activity_id: props.activityId },
        );
        fetchCalendarMonth(month, activityToken, fetchedMonths);
    };

    const hidePricesInCalendar = behaviors.detail.hidePricesInCalendar || isHidePriceInCalendar;

    const displayPriceLabels = isOfferLevel ? offerLevelPriceLabels : priceLabels;
    const displayUnavailableDates = isOfferLevel ? offerLevelUnavailableDates : unavailableDates;
    // while the availability is loading, all dates should available to display correct month
    const isSelectedDateUnavailable = !isAvailabilityLoading && displayUnavailableDates.some((date) => date.toDateString() === selectedDate.toDateString());
    const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
    const displayPriceLabelsDictionary = (displayPriceLabels || []).reduce<Record<string, string>>((acc, label) => {
        const dateKey = label.date.toLocaleDateString(props.locale, options);
        acc[dateKey] = label.label;
        return acc;
    }, {});

    // pb="s" is added to fix the spacing issue between the calendar and the divider in Firefox
    return (
        <>
            <Box ref={popoverCalendarRef} pb="8">
                {/* TODO: ACTB-1372 update drone date picker to assign header level in calendar month on B */}
                <DatePicker
                    data-element-name="activities-popover-calendar"
                    data-testid="activities-popover-calendar"
                    variant="single"
                    ref={datePickerRef}
                    date={!isSelectedDateUnavailable ? selectedDate : undefined}
                    isSingleDate={true}
                    headingTag="h5"
                    onDateSelect={onDateSelect}
                    disabledDates={displayUnavailableDates}
                    labels={hidePricesInCalendar ? [] : displayPriceLabels}
                    isLoading={hidePricesInCalendar ? false : isLoading}
                    onMonthChange={onMonthChange}
                    startDate={getTodayDate()}
                    getDateAriaLabel={(forDate) => {
                        const date = forDate.toLocaleDateString(props.locale, options);
                        const label = displayPriceLabelsDictionary[date] || '';
                        return StringFormatter.format(props.cms[331992], date, label, props.currency);
                    }}
                />
            </Box>
            {!hidePricesInCalendar && (
                <>
                    <Divider my="8" />
                    <Box py="8" px={props.isPopover ? '8' : '0'}>
                        <CalendarPriceInfoText />
                    </Box>
                </>
            )}
        </>
    );
};
PopoverCalendar.displayName = 'PopoverCalendar';

export default connect(mapStateToProps, mapDispatchToProps)(PopoverCalendar);
